import type {
  FileValidationResult,
  ImportRowErrorNewFormat,
  ImportResult,
  ParsedErrorMessage,
  ErrorWithString,
} from './ImportButton.types';

/**
 * Validates that the file is a CSV and not empty
 */
export function validateFile(file: File | null): FileValidationResult {
  if (!file) {
    return { isValid: false };
  }

  if (!file.name.toLowerCase().endsWith('.csv')) {
    return {
      isValid: false,
      errorMessage: 'Only .csv files are allowed',
    };
  }

  if (file.size === 0) {
    return {
      isValid: false,
      errorMessage: 'File is empty',
    };
  }

  return { isValid: true };
}

/**
 * Creates a file input element and triggers the file picker
 */
export function createFileInput(onFileSelected: (file: File) => void): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.csv,text/csv';
  input.onchange = () => {
    const file = input.files?.[0];
    if (file) {
      onFileSelected(file);
    }
  };
  input.click();
}

/**
 * Formats import success message
 */
export function formatImportSuccessMessage(result: ImportResult): string {
  return `Import complete: processed ${result.processed} (created ${result.created} / updated ${result.updated}).`;
}

/**
 * Parses error object from import row error into a readable message
 */
export function parseRowError(error: ImportRowErrorNewFormat): ParsedErrorMessage {
  // Try to parse structured error object
  if (error.error && typeof error.error === 'object') {
    const messages = Object.entries(error.error).flatMap(([field, val]) => {
      // Normalize value to string array
      const msgArray = Array.isArray(val)
        ? val.map((item) => (typeof item === 'object' && item !== null && 'string' in item ? item.string : String(item)))
        : val !== undefined ? [String(val)] : [];

      return msgArray.map((message) => `• ${field}: ${message}`);
    });

    if (messages.length > 0) {
      return { message: messages.join('\n') };
    }
  }

  // Fallback to other message sources
  return {
    message: error.message || (typeof error.error === 'string' ? error.error : 'Unknown error')
  };
}
